import { fetchRequest } from './';
import { auth } from './auth';
import { utcDateString } from './date-time';
import { CodeSet, CodeType, Diagnosis, Procedure } from './domain';
import { CRITICAL_CARE_CODE_1, CRITICAL_CARE_CODE_2, CRITICAL_CARE_CODE_3, procedure } from './procedure';
import { WorksheetAction, worksheetMessage, WorksheetMessage, WorksheetResult } from './worksheet-service';

export interface Code {
  code: string;
  codes?: string[];
  type?: CodeType | string;
  codeType?: string;
  serviceDate?: string;
  locationCode?: string;
  departmentCode?: string;
  worksheetType?: string;
  page?: number;
  pageSize?: number;
}

type CodesetMessage = WorksheetMessage<Code>;
type CodeArgs = Partial<Code> & {
  dx?: Diagnosis;
  cpt?: Procedure;
  fullText?: boolean;
  workflow?: string;
};

function resolveCodeType(type: string) {
  //convert true code types
  let codeType = type || '';

  if (codeType.indexOf('ICD') > -1) {
    codeType = 'ICD_10';
  } else if (codeType.indexOf('CPT') > -1) {
    codeType = 'PROC_CPT';
  } else if (codeType.indexOf('HCPCS') > -1) {
    codeType = 'PROC_HCPCS';
  }
  return codeType;
}

export function getDXorProcedures(code: CodeArgs): CodesetMessage {
  code.codeType = undefined;

  const serviceDate = code.serviceDate ? code.serviceDate : utcDateString(new Date());
  let codes: any[] = code.codes?.length ? code.codes : [code.code];
  codes = codes.map((a) => ({
    serviceDate,
    code: (a || '').replace('\\', '').replace('/', '').toUpperCase(),
  }));

  return worksheetMessage(
    {
      codes,
    },
    'getdxorprocedures',
    'worksheet',
    code.workflow
  );
}

export type CodesetResult = WorksheetResult<CodeSet>;

export const getCode = fetchRequest<CodeArgs, CodesetResult>({
  method: 'post',
  body: (args: any) => codesetMessage({ ...args }, 'getcodebycodeandtype'),
});

export const getmodifier = fetchRequest<CodeArgs, CodesetResult>({
  method: 'post',
  body: (args: any) => codesetMessage({ ...(args as any) }, 'getmodifier'),
});

export const getmodifiers = fetchRequest<{ locationCode: string; serviceDate: string }, { code: string; locationCode: string }[]>({
  method: 'get',
  url: (c) => `/coding-codesets/getmodifiers?locationcode=${c.locationCode}&servicedate=${utcDateString(c.serviceDate || new Date())}`,
});

export const findDxOrProcedureRequest = fetchRequest<CodeArgs, CodesetResult>({
  method: 'post',
  body: (args: CodeArgs) => {
    const type = args.codeType || args.type;

    if (type) {
      return codesetMessage({ ...args, ...{ type } }, 'getcodebycodeandtype');
    }
    return codesetMessage(args, 'getdxorprocedure');
  },
});

export function codesetMessage(code: CodeArgs, action: WorksheetAction = 'getdxorprocedure'): CodesetMessage {
  return worksheetMessage(
    {
      ...code,
      ...{
        code: (code.code || '').replace('\\', '').replace('/', '').toUpperCase(),
        dx: undefined,
        cpt: undefined,
      },
    },
    action,
    'worksheet',
    code.workflow
  );
}

export type CodesetsResult = WorksheetResult<CodeSet[]>;
export const findDxOrProcedureRequestList = fetchRequest<CodeArgs, CodesetsResult | CodesetResult>({
  method: 'post',
  body: (args: CodeArgs) => {
    const type = resolveCodeType(args.codeType || args.type);
    const serviceDate = defaultServiceDate(args.serviceDate);
    const codes = args.codes ? args.codes : [args.code];
    const { dx, cpt } = args;

    if (cpt && type !== 'ICD_10') {
      return codesetMessage({ ...args, ...{ type, serviceDate } }, 'getprocedure');
    }
  
    // TODO remove request if its unecessary
    if (dx) {
      return codesetMessage({ ...args, ...{ type, serviceDate } }, 'getcodebycodeandtype');
    }
    if (type) {
      return codesetMessage({ ...args, ...{ type, serviceDate }, codes }, 'getcodebycodeandtype');
    }

    return getDXorProcedures({ ...args, codes, serviceDate });
  },
});

export async function findDxOrProcedures(args: CodeArgs, onError: (a: string) => void, workflow?: string): Promise<CodeSet[]> {
  const response = await findDxOrProcedureRequestList(args);
  if (!response?.success) {
    onError((response?.errors || []).join(' '));
    return [];
  }
  const list = Array.isArray(response.result) ? response.result : [response.result];
  return list;
}

export const loadCptRequest = fetchRequest<CodeArgs, CodesetResult>({
  method: 'post',
  body: (args: CodeArgs) => codesetMessage({ ...args, ...{ type: 'PROC_CPT' } }, 'getcodebycodeandtype'),
});

export async function loadCpts(cpts: string[]): Promise<CodeSet[]> {
  const serviceDate = defaultServiceDate();
  const values = await Promise.all(cpts.map((code) => loadCptRequest({ code, serviceDate, type: 'PROC_CPT' })));

  return values.filter((r) => r && r.result).map((r) => r.result);
}

const critCareCpts: Record<string, Procedure> = {};
export async function loadCritCareCpts(): Promise<Record<string, Procedure>> {
  if (!critCareCpts[CRITICAL_CARE_CODE_1]) {
    if (auth.isAuthenticated()) {
      const sets = await loadCpts([CRITICAL_CARE_CODE_1, CRITICAL_CARE_CODE_2]);
      sets.forEach((v: CodeSet) => {
        critCareCpts[v.code] = procedure(v, null);
      });
    }
  }
  return critCareCpts;
}

let today: string = undefined;
function defaultServiceDate(date?: string) {
  if (date) {
    return date;
  }
  if (!today) {
    today = utcDateString(new Date());
  }
  return today;
}
const autoCompleteEndpoint = () => '/coding-codesets/autocomplete';

export const autoCompleteRequest = fetchRequest<CodeArgs, any>({
  method: 'get',
  url: (c: CodeArgs) =>
    `${autoCompleteEndpoint()}?query=${c.code}&page=${c.page || 0}&pageSize=${c.pageSize || 10}&fullText=${c.fullText || false}&servicedate=${defaultServiceDate(c.serviceDate)}`,
});

export function updateElement(el: Procedure | Diagnosis | any, c: CodeSet): void {
  let codeChanged = false;
  if (el.diagnosisCode) {
    codeChanged = el.diagnosisCode !== c.code;
    el.diagnosisCode = c.code;
    el.dxDescription = c.description.longDescription;
  } else {
    codeChanged = el.cptCode !== c.code;
    el.cptCode = c.code;
    el.description = c.description.longDescription;
  }
  if (codeChanged) {
    el.stickyNote = '';
    el.note = null;
  }
}
